import { LineItem } from "@/utils/types/types";
import axios from "axios";

// ── Checkout source-file existence guard (2026-08-12, T-50765) ───────────────
// A cart item can outlive the S3 objects behind it: the raw upload (and crop)
// referenced by itemJson can be deleted from the uploads page, cleaned up, or
// simply never have finished processing — while the cart row keeps the URL.
// Order T-50765 (2026-08-12) finalized exactly such a cart: the backend
// jobbuilder's import tasks then died DOWNLOAD_403 (S3 answers 403, not 404,
// for a missing key), no zip was built, and staff found an incomplete order
// folder. This guard HEAD-checks every item's source files BEFORE the customer
// can pay, so a doomed order is caught while the customer is still present to
// re-upload.
//
// FAIL-OPEN BY DESIGN: only a definite 403/404 *response* marks a file
// missing. Timeouts, network errors, and CORS-opaque failures all pass — a
// flaky connection or a CDN hiccup must never block a real sale. The
// authoritative server-side finalize check (queued backend hardening) remains
// the backstop this guard fronts for.

export type MissingSourceFile = {
  lineItemId: string;
  /** Human-recognizable file name (basename of the missing object). */
  fileName: string;
  url: string;
  status: number;
};

const HEAD_TIMEOUT_MS = 4000;

function fileNameFromUrl(url: string): string {
  try {
    const base = new URL(url).pathname.split("/").pop() || url;
    return decodeURIComponent(base);
  } catch {
    return url;
  }
}

/** Definite-missing check for one URL. Resolves to a status code ONLY when the
 *  server answered 403/404; every other outcome (2xx, other statuses, opaque
 *  network/CORS errors, timeout) resolves to null = "do not block". */
async function probeUrl(url: string): Promise<number | null> {
  try {
    await axios.head(url, { timeout: HEAD_TIMEOUT_MS });
    return null;
  } catch (error) {
    const status = axios.isAxiosError(error)
      ? error.response?.status
      : undefined;
    return status === 403 || status === 404 ? status : null;
  }
}

export async function findMissingSourceFiles(
  lineItems: LineItem[]
): Promise<MissingSourceFile[]> {
  const checks: Promise<MissingSourceFile | null>[] = [];

  for (const item of lineItems || []) {
    const itemJson: any = item?.itemJson;
    if (!itemJson) continue;
    // Only the files the order pipeline actually imports: the raw original
    // and, when the item was cropped, the crop. Preview/compressed images are
    // cart cosmetics — never block payment on those. Items without a
    // rawImageUrl (services, supplied work) have nothing to verify.
    const urls: string[] = [];
    if (itemJson.rawImageUrl) urls.push(itemJson.rawImageUrl);
    if (itemJson.croppedImageUrl) urls.push(itemJson.croppedImageUrl);
    for (const url of urls) {
      checks.push(
        probeUrl(url).then(status =>
          status === null
            ? null
            : {
                lineItemId: String(item.id ?? ""),
                fileName: fileNameFromUrl(url),
                url,
                status,
              }
        )
      );
    }
  }

  if (checks.length === 0) return [];

  const results = await Promise.all(checks);
  const missing = results.filter((r): r is MissingSourceFile => r !== null);

  if (missing.length > 0) {
    // The shop must hear about every hit even when the customer walks away —
    // this event firing means a broken order was PREVENTED.
    (window as any).Sentry?.captureMessage(
      "checkout blocked: cart item source file missing (would have produced a T-50765-style broken order)",
      { level: "warning", extra: { missing } }
    );
  }
  return missing;
}
